import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { noop, tap } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import moment from 'moment';
import { PropertyTenantsService } from '../../tenants.service';
import { Property } from 'app/modules/projects/projects.types';
import { ProjectType, PropertyType, ProjectStage } from 'app/core/models/account.types';
import {
  PropertyTenant,
  PropertyUnit,
  RENT_ESCALATION_UNITS,
  RentEscalation,
} from 'app/modules/projects/details/tenants/tenants.types';

@Component({
  selector: 'property-tenants-rent-escalation',
  templateUrl: './rent-escalation.component.html',
  styleUrls: ['./rent-escalation.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'L',
        },
        display: {
          dateInput: 'L',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'L',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
})
export class PropertyTenantsRentEscalationComponent extends OnDestroyMixin implements OnInit {
  propertyTenantForm: UntypedFormGroup;
  projectTypes: ProjectType[];
  propertyTypes: PropertyType[];
  projectStages: ProjectStage[];
  property: Property;
  propertyUnit: PropertyUnit;
  propertyTenant: PropertyTenant;
  escalations: any;
  public uploadedFile: any;
  public isLoading: boolean;
  options: [];
  rentEscalationsForm = new UntypedFormGroup({ rentEscalations: new UntypedFormArray([]) });
  ui = { isLoading: true };
  rentEscalationUnits: any;

  get rentEscalations(): UntypedFormArray {
    return this.rentEscalationsForm.controls['rentEscalations'] as UntypedFormArray;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly matDialogRef: MatDialogRef<PropertyTenantsRentEscalationComponent>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly propertyTenantsService: PropertyTenantsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.rentEscalationUnits = RENT_ESCALATION_UNITS;
    this.buildForm();
  }

  private buildForm(): void {
    this.property = this.data.property;
    this.propertyUnit = this.data.propertyUnit;
    this.propertyTenant = this.data.propertyTenant;
    this.escalations = this.propertyTenant.rent_escalations;

    this.escalations = this.escalations.sort((a, b) => a?.start_month - b?.start_month);

    for (const rentEscalation of this.escalations) {
      let control = new UntypedFormGroup({
        start_date: new UntypedFormControl(moment(rentEscalation.start_date).utc()),
        start_month: new UntypedFormControl(rentEscalation.start_month),
        amount: new UntypedFormControl(rentEscalation.amount),
        escalation_units: new UntypedFormControl(rentEscalation.escalation_units),
        hidden: new UntypedFormControl(rentEscalation),
        id: new UntypedFormControl(rentEscalation.id),
      });
      this.rentEscalations.push(control);
      this.attachStartMonthChanges(control);
    }
  }

  public discard(): void {
    this.matDialogRef.close();
  }

  public save(): void {
    let rentEscalations = [];

    for (let rs of this.rentEscalations.controls) {
      const rentEscalation = rs.get('hidden').value as RentEscalation;
      const start_month = rs.get('start_month').value as number;
      const start_date = rs.get('start_date').value as moment.Moment;
      const amount = rs.get('amount').value as number;
      const escalation_units = rs.get('escalation_units').value as number;

      rentEscalations.push({
        ...rentEscalation,
        start_month: start_month,
        start_date: start_date.toISOString(),
        amount: amount,
        escalation_units: escalation_units,
      } as RentEscalation);
    }
    this.propertyTenant.rent_escalations = rentEscalations;

    this.propertyTenantsService
      .updatePropertyTenant(this.propertyUnit.id, this.propertyTenant)
      .pipe(
        untilComponentDestroyed(this),
        tap(() => this.discard()),
      )
      .subscribe(noop);
  }

  attachStartMonthChanges(control: AbstractControl): void {
    control
      .get('start_month')
      .valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe((value) => {
        if (this.propertyTenant.lease_start_date) {
          let leaseStartDate = moment(this.propertyTenant.lease_start_date);
          let startDate = leaseStartDate.add(value, 'months');
          control.get('start_date').setValue(startDate, { emitEvent: false });
        }
      });

    control
      .get('start_date')
      .valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        if (this.propertyTenant.lease_start_date) {
          let leaseStartDate = moment(this.propertyTenant.lease_start_date);
          let diffMonths = control.get('start_date').value.diff(leaseStartDate, 'months');
          control.get('start_month').setValue(diffMonths, { emitEvent: false });
        }
      });
  }

  add(): void {
    let control = new UntypedFormGroup({
      start_date: new UntypedFormControl(''),
      start_month: new UntypedFormControl(null),
      amount: new UntypedFormControl(null),
      escalation_units: new UntypedFormControl('amount_per_month'),
      id: new UntypedFormControl(null),
      hidden: new UntypedFormControl(null),
    });

    this.rentEscalations.push(control);
    this.attachStartMonthChanges(control);
  }

  remove(index: number): void {
    const rentEscalation = this.rentEscalations.at(index).get('hidden').value as any;

    if (rentEscalation) {
      this.propertyTenantsService
        .deleteRentEscalation(this.property.id, this.propertyUnit.id, this.propertyTenant.id, rentEscalation.id)
        .subscribe(() => {
          this.rentEscalations.removeAt(index);
        });
    } else {
      this.rentEscalations.removeAt(index);
      return;
    }
  }
}
